import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { NotifLang } from '@/types/admin.types';
import {
  classifyTranslateError, planTranslation, runTranslation,
  type FieldMap, type FilledMap, type TranslateErrorKind,
} from './catalogTranslation';

const ERROR_KEYS: Record<TranslateErrorKind, string> = {
  session: 'admin.sessionExpiredError',
  wait: 'admin.catalogTranslateErrorWait',
  retry: 'admin.catalogTranslateErrorRetry',
};

interface CatalogTranslateButtonProps {
  /** Campos traducibles del modal, cada uno con sus 7 idiomas. */
  fields: FieldMap;
  /** Idioma del chip activo: es el origen preferido si ya tiene texto. */
  activeLang: NotifLang;
  /** Recibe solo lo traducido para idiomas que estaban vacíos; el modal lo aplica con `mergeMissing`. */
  onFilled: (filled: FilledMap) => void;
}

/**
 * Botón "Traducir a los demás idiomas" de los modales del Catálogo de Fundamentales. El mensaje de
 * error se muestra debajo del botón y se resuelve con el idioma activo de la interfaz.
 */
export function CatalogTranslateButton({ fields, activeLang, onFilled }: Readonly<CatalogTranslateButtonProps>) {
  const { t } = useTranslation();
  const [translating, setTranslating] = useState(false);
  const [errorKind, setErrorKind] = useState<TranslateErrorKind | null>(null);

  const batches = planTranslation(fields, activeLang);

  const handleTranslate = async () => {
    if (batches.length === 0 || translating) return;
    setErrorKind(null);
    setTranslating(true);
    try {
      const { filled, missing } = await runTranslation(batches);
      onFilled(filled);
      if (missing > 0) setErrorKind('retry');
    } catch (error) {
      setErrorKind(classifyTranslateError(error));
    } finally {
      setTranslating(false);
    }
  };

  return (
    <div className="adm-translate-row">
      <button
        type="button"
        className="btn btn--secondary btn--sm"
        disabled={batches.length === 0 || translating}
        onClick={handleTranslate}
        title={batches.length === 0 ? t('admin.catalogTranslateHint') : undefined}
      >
        <i className={`fas ${translating ? 'fa-spinner fa-spin' : 'fa-language'}`} />
        {' '}
        {translating ? t('admin.catalogTranslating') : t('admin.catalogTranslateBtn')}
      </button>
      {errorKind && (
        <p role="alert" style={{ margin: '6px 0 0', fontSize: '0.72rem', color: 'var(--cp-red, #E11F26)' }}>
          <i className="fas fa-triangle-exclamation" style={{ marginRight: 4 }} />
          {t(ERROR_KEYS[errorKind])}
        </p>
      )}
    </div>
  );
}
